import type { ComponentRef, OnDestroy } from '@angular/core';
import { Injectable } from '@angular/core';

import { merge, of } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import type { LinkInput, OverlayPosition, LinkCallbacks } from '@core/prosemirror-legacy';
import { WysiwygComponentPortalService } from '@core/prosemirror-legacy/angular';

import { LinkInputComponent } from './link-input/link-input.component';
import { LinkTooltipComponent } from './link-tooltip/link-tooltip.component';

@Injectable()
export class LinkComponentService
  extends WysiwygComponentPortalService<LinkTooltipComponent | LinkInputComponent>
  implements LinkInput, OnDestroy
{
  protected overlayConfig = {
    hasBackdrop: true,
    backdropClass: 'cdk-overlay-transparent-backdrop',
  };

  private preferredPositions = [
    <const>{
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
    },
  ];

  public openLinkTooltip(
    position: OverlayPosition,
    initialValue: string | null,
    callbacks: LinkCallbacks,
  ) {
    this.close();
    this.openComponent(LinkTooltipComponent, position, this.preferredPositions);
    const { open, remove, edit } = (<ComponentRef<LinkTooltipComponent>>this.componentRef).instance;

    const close$ = this.overlayRef!.backdropClick();

    open
      .pipe(take(1), takeUntil(merge(close$, edit, this.closeComponents$)))
      .subscribe(() => callbacks.open());
    remove.pipe(take(1), takeUntil(merge(close$, edit, this.closeComponents$))).subscribe(() => {
      callbacks.remove();
      callbacks.exit();
    });
    edit
      .pipe(take(1), takeUntil(merge(close$, this.closeComponents$)))
      .subscribe(() => this.openLinkInput(position, initialValue, callbacks));

    close$
      .pipe(take(1), takeUntil(merge(edit, remove, this.closeComponents$)))
      .subscribe(() => callbacks.exit());

    this.componentRef!.changeDetectorRef.detectChanges();
    this.updatePosition(position);
  }

  public openLinkInput(
    position: OverlayPosition,
    initialValue: string | null,
    callbacks: LinkCallbacks,
  ) {
    this.close();
    this.openComponent(LinkInputComponent, position, this.preferredPositions);
    const { formControl, save } = (<ComponentRef<LinkInputComponent>>this.componentRef).instance;

    formControl.setValue(initialValue);
    const close$ = this.overlayRef!.backdropClick();

    save
      .pipe(
        take(1),
        switchMap(() => of(formControl.value)),
        takeUntil(merge(close$, this.closeComponents$)),
      )
      .subscribe(value => {
        if (value) {
          callbacks.setValue(value);
        } else {
          callbacks.remove();
        }
        callbacks.exit();
      });

    close$
      .pipe(take(1), takeUntil(merge(save, this.closeComponents$)))
      .subscribe(() => callbacks.exit());

    this.componentRef!.changeDetectorRef.detectChanges();
    this.updatePosition(position);
  }
}
